import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { User, Shield, Bell, Palette, Keyboard, Loader2, Check, Sun } from 'lucide-react';
import { usersApi } from '../api/users';
import { toast } from '../components/Toast';

type TabId = 'profile' | 'security' | 'notifications' | 'appearance' | 'shortcuts';

const TABS: { id: TabId; label: string; icon: React.ReactNode }[] = [
  { id: 'profile', label: '资料', icon: <User className="h-4 w-4" /> },
  { id: 'security', label: '安全', icon: <Shield className="h-4 w-4" /> },
  { id: 'notifications', label: '通知偏好', icon: <Bell className="h-4 w-4" /> },
  { id: 'appearance', label: '外观', icon: <Palette className="h-4 w-4" /> },
  { id: 'shortcuts', label: '快捷键', icon: <Keyboard className="h-4 w-4" /> },
];

export function PersonalSettingsPage() {
  const queryClient = useQueryClient();
  const { data: me, isLoading } = useQuery({
    queryKey: ['me'],
    queryFn: usersApi.me,
  });

  const [tab, setTab] = useState<TabId>('profile');

  // 资料表单
  const [nickname, setNickname] = useState('');
  useEffect(() => {
    if (me) setNickname(me.nickname || '');
  }, [me]);

  const updateProfileMutation = useMutation({
    mutationFn: () => usersApi.updateProfile({ nickname: nickname.trim() || '未命名用户' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me'] });
      toast.success('资料已更新');
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : '保存失败');
    },
  });

  // 安全 - 修改密码
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const changePasswordMutation = useMutation({
    mutationFn: () => usersApi.changePassword({ oldPassword, newPassword }),
    onSuccess: () => {
      toast.success('密码已修改');
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : '修改失败');
    },
  });

  function handlePasswordSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('两次密码不一致');
      return;
    }
    changePasswordMutation.mutate();
  }

  // 通知偏好
  const [emailNotify, setEmailNotify] = useState(true);
  const [theme] = useState<'light'>('light');
  const [locale, setLocale] = useState('zh-CN');
  useEffect(() => {
    if (me?.preferences) {
      setEmailNotify(me.preferences.emailNotify);
      setLocale(me.preferences.locale);
    }
  }, [me]);

  const updatePreferencesMutation = useMutation({
    mutationFn: () =>
      usersApi.updatePreferences({ emailNotify, theme, locale }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me'] });
      toast.success('偏好已更新');
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : '保存失败');
    },
  });

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-text-tertiary" />
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-4xl gap-8 px-8 py-8">
      {/* 侧边 Tab */}
      <nav className="w-44 shrink-0 space-y-1">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
              tab === t.id
                ? 'bg-accent-50 text-accent'
                : 'text-text-secondary hover:bg-bg-subtle hover:text-text-primary'
            }`}
          >
            {t.icon}
            {t.label}
          </button>
        ))}
      </nav>

      {/* 内容区 */}
      <div className="flex-1">
        {/* 资料 */}
        {tab === 'profile' && (
          <div className="card p-6">
            <h2 className="text-base font-medium">个人资料</h2>
            <div className="mt-4 space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium">邮箱</label>
                <input className="input" value={me?.email || ''} disabled />
                <p className="mt-1 text-xs text-text-tertiary">
                  {me?.emailVerifiedAt ? (
                    <span className="inline-flex items-center gap-1 text-success">
                      <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-success/10">
                        <svg className="h-2.5 w-2.5" viewBox="0 0 12 12" fill="none" aria-hidden>
                          <path d="M3 6l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      已验证
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-text-tertiary">
                      <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-bg-subtle">
                        <svg className="h-2.5 w-2.5" viewBox="0 0 12 12" fill="none" aria-hidden>
                          <path d="M4 4l4 4M8 4l-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </span>
                      未验证
                    </span>
                  )}
                </p>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium" htmlFor="nickname-input">
                  昵称
                </label>
                <input
                  id="nickname-input"
                  className="input"
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  maxLength={32}
                />
              </div>
              <div className="flex justify-end pt-2">
                <button
                  type="button"
                  className="btn btn-primary btn-md"
                  disabled={updateProfileMutation.isPending}
                  onClick={() => updateProfileMutation.mutate()}
                >
                  {updateProfileMutation.isPending ? '保存中…' : '保存'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 安全 */}
        {tab === 'security' && (
          <div className="card p-6">
            <h2 className="text-base font-medium">修改密码</h2>
            <form onSubmit={handlePasswordSubmit} className="mt-4 space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium" htmlFor="old-password">
                  原密码
                </label>
                <input
                  id="old-password"
                  type="password"
                  className="input"
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium" htmlFor="new-password">
                  新密码
                </label>
                <input
                  id="new-password"
                  type="password"
                  className="input"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                />
                <p className="mt-1 text-xs text-text-tertiary">至少 8 位，需含字母和数字</p>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium" htmlFor="confirm-password">
                  确认新密码
                </label>
                <input
                  id="confirm-password"
                  type="password"
                  className="input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>
              <div className="flex justify-end pt-2">
                <button
                  type="submit"
                  className="btn btn-primary btn-md"
                  disabled={changePasswordMutation.isPending}
                >
                  {changePasswordMutation.isPending ? '修改中…' : '修改密码'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* 通知偏好 */}
        {tab === 'notifications' && (
          <div className="card p-6">
            <h2 className="text-base font-medium">通知偏好</h2>
            <div className="mt-4 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium">邮件通知</div>
                  <p className="text-xs text-text-tertiary">接收评论回复、@提及等邮件通知</p>
                </div>
                <label className="relative inline-flex h-6 w-11 cursor-pointer items-center">
                  <input
                    type="checkbox"
                    className="peer sr-only"
                    checked={emailNotify}
                    onChange={(e) => setEmailNotify(e.target.checked)}
                  />
                  <span className="absolute inset-0 rounded-full bg-bg-subtle transition-colors peer-checked:bg-accent" />
                  <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform peer-checked:translate-x-5" />
                </label>
              </div>
              <div className="flex justify-end pt-2">
                <button
                  type="button"
                  className="btn btn-primary btn-md"
                  disabled={updatePreferencesMutation.isPending}
                  onClick={() => updatePreferencesMutation.mutate()}
                >
                  {updatePreferencesMutation.isPending ? '保存中…' : '保存'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 外观 */}
        {tab === 'appearance' && (
          <div className="card p-6">
            <h2 className="text-base font-medium">外观</h2>
            <div className="mt-4 space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium">主题</label>
                <div className="flex gap-2">
                  <div className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-accent bg-accent-50 px-4 py-3 text-sm font-medium text-accent">
                    <Sun className="h-4 w-4" />
                    浅色
                    <Check className="h-4 w-4" />
                  </div>
                </div>
                <p className="mt-1.5 text-xs text-text-tertiary">当前仅支持浅色模式</p>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">语言</label>
                <select
                  className="input"
                  value={locale}
                  onChange={(e) => setLocale(e.target.value)}
                >
                  <option value="zh-CN">简体中文</option>
                  <option value="en-US">English</option>
                </select>
              </div>
              <div className="flex justify-end pt-2">
                <button
                  type="button"
                  className="btn btn-primary btn-md"
                  disabled={updatePreferencesMutation.isPending}
                  onClick={() => updatePreferencesMutation.mutate()}
                >
                  {updatePreferencesMutation.isPending ? '保存中…' : '保存'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 快捷键 */}
        {tab === 'shortcuts' && (
          <div className="card p-6">
            <h2 className="text-base font-medium">快捷键</h2>
            <div className="mt-4 space-y-3">
              {[
                { keys: '⌘K', desc: '全局搜索' },
                { keys: '⌘B', desc: '加粗' },
                { keys: '⌘I', desc: '斜体' },
                { keys: '⌘U', desc: '下划线' },
                { keys: '⌘⇧S', desc: '保存文档' },
                { keys: '⌘Z', desc: '撤销' },
                { keys: '⌘⇧Z', desc: '重做' },
                { keys: '⌘⇧K', desc: '插入链接' },
                { keys: '⌘⇧7', desc: '有序列表' },
                { keys: '⌘⇧8', desc: '无序列表' },
                { keys: '⌘⇧9', desc: '代码块' },
                { keys: '⌘⇧M', desc: '切换侧边栏' },
              ].map((s) => (
                <div
                  key={s.keys}
                  className="flex items-center justify-between rounded-lg bg-bg-subtle px-3 py-2"
                >
                  <span className="text-sm">{s.desc}</span>
                  <kbd className="rounded border border-border bg-bg-surface px-2 py-0.5 text-xs font-medium text-text-secondary">
                    {s.keys}
                  </kbd>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}